import React, { useEffect, useState } from 'react'
import { useBlocker } from '@msaf/router-react'
import { Button, ButtonStyle, Modal, ModalProps } from '../../common'

export interface ConfirmTransitionProps extends Omit<ModalProps, 'isOpen' | 'onRequestClose' | 'body' | 'children'> {
  when?: boolean
  confirmText?: string
  cancelText?: string
  modifiedFields?: string[]
  actionConfig: Array<{
    type: 'confirm' | 'cancel'
    label: string
    buttonStyle: ButtonStyle
    onClick?: () => Promise<void>
    key?: string
  }>
}

export function ConfirmTransition({
  when,
  contentLabel = 'Unsaved changes confirmation dialog',
  heading = 'Unsaved changes',
  bodyText,
  modifiedFields = [],
  confirmText = 'Discard changes',
  cancelText = 'Continue editing',
  actionConfig,
  ...props
}: ConfirmTransitionProps) {
  const [open, setOpen] = useState(false)

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => !!when && currentLocation.pathname !== nextLocation.pathname,
  )

  useEffect(() => {
    if (when && blocker.state === 'blocked') setOpen(true)
  }, [blocker.state, when])

  const onClick = async (type: 'confirm' | 'cancel', callbackFn?: () => Promise<void>) => {
    await callbackFn?.()
    switch (type) {
      case 'confirm':
        blocker.proceed?.()
        break
      case 'cancel':
        blocker.reset?.()
        break
    }
    setOpen(false)
  }

  const actions = actionConfig.map((config) => {
    const { type, onClick: callbackFn, ...buttonProps } = config
    return (
      <Button {...buttonProps} key={buttonProps.key ?? buttonProps.label} onClick={() => onClick(type, callbackFn)} />
    )
  })

  // If there are no modified fields, we don't need to show the list of modified fields
  const newBodyText =
    modifiedFields.length === 0 ? (
      bodyText ?? 'This page has unsaved changes, are you sure you want to continue?'
    ) : (
      <>
        <p>{bodyText ?? 'This page has unsaved changes to the following fields. Are you sure you want to continue?'}</p>
        <ul>
          {modifiedFields.map((field) => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      </>
    )

  return (
    <Modal
      {...props}
      isOpen={open}
      onRequestClose={() => setOpen(false)}
      contentLabel={contentLabel}
      heading={heading}
      buttons={<>{actions}</>}
      body={newBodyText}
    />
  )
}
